import { useState, useEffect } from 'react'
import { api } from '../services/api'
import { getCurrentUser } from '../services/auth'

const STATUS_OPCOES = ['gerado', 'assinado', 'ativo', 'encerrado', 'cancelado']

const formatarData = iso => {
  if (!iso) return '—'
  const [ano, mes, dia] = iso.split('T')[0].split('-')
  return `${dia}/${mes}/${ano}`
}

export default function ListaContratos() {
  const user = getCurrentUser()
  const isMaster = user?.role === 'master'

  const [contratos, setContratos] = useState([])
  const [termos, setTermos]       = useState([])
  const [loading, setLoading]     = useState(true)
  const [erro, setErro]           = useState('')
  const [atualizando, setAtualizando] = useState(null)
  const [baixando, setBaixando]   = useState(null)
  const [aba, setAba]             = useState('contratos')

  const carregar = () => {
    setLoading(true)
    setErro('')
    Promise.all([
      api.contratos.listar(),
      api.termos.listar(),
    ])
      .then(([c, t]) => {
        setContratos(Array.isArray(c) ? c : [])
        setTermos(Array.isArray(t) ? t : [])
      })
      .catch(e => setErro(e.message))
      .finally(() => setLoading(false))
  }

  useEffect(carregar, [])

  const mudarStatus = async (id, novoStatus) => {
    setAtualizando(id)
    try {
      await api.contratos.atualizarStatus(id, novoStatus)
      setContratos(cs => cs.map(c => c.id === id ? { ...c, status: novoStatus } : c))
    } catch (e) {
      alert('Erro ao atualizar status: ' + e.message)
    } finally {
      setAtualizando(null)
    }
  }

  const baixarContrato = async (contrato) => {
    setBaixando(contrato.id)
    try {
      await api.contratos.baixar(contrato.id, contrato.arquivo_path)
    } catch (e) {
      alert('Erro ao baixar: ' + e.message)
    } finally {
      setBaixando(null)
    }
  }

  const baixarTermo = async (termo) => {
    setBaixando(termo.id)
    try {
      await api.termos.baixar(termo.id, termo.arquivo_path)
    } catch (e) {
      alert('Erro ao baixar: ' + e.message)
    } finally {
      setBaixando(null)
    }
  }

  const tabStyle = (key) => ({
    padding: '6px 18px',
    fontSize: '12px',
    borderRadius: '8px',
    cursor: 'pointer',
    border: aba === key ? '1px solid var(--active-border)' : '1px solid var(--btn-border)',
    background: aba === key ? 'var(--active-bg)' : 'var(--btn-bg)',
    color: aba === key ? 'var(--active-border)' : 'var(--btn-text)',
    fontFamily: 'inherit',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  })

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '20px', fontWeight: 'normal', letterSpacing: '0.06em', margin: 0, color: 'var(--text-primary)' }}>
          HISTÓRICO
        </h1>
        <button onClick={carregar} style={{ fontSize: '12px', padding: '6px 16px' }}>Atualizar</button>
      </div>

      {/* Abas */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <button style={tabStyle('contratos')} onClick={() => setAba('contratos')}>
          Contratos ({contratos.length})
        </button>
        <button style={tabStyle('termos')} onClick={() => setAba('termos')}>
          Conf. de Dívida ({termos.length})
        </button>
      </div>

      <div className="card">
        {loading ? (
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Carregando...</p>
        ) : erro ? (
          <div className="badge-err">{erro}</div>
        ) : aba === 'contratos' ? (
          contratos.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Nenhum contrato gerado ainda.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  {['Cliente', 'Veículo', 'Início', 'Valor semanal', 'Gerado por', 'Status', ''].map(h => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {contratos.map(c => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 'bold' }}>{c.clientes?.nome || '—'}</td>
                    <td>{c.veiculos?.placa} · {c.veiculos?.modelo}</td>
                    <td>{formatarData(c.data_inicio)}</td>
                    <td>R$ {parseFloat(c.valor_semanal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{c.usuarios?.nome || '—'}</td>
                    <td>
                      {isMaster ? (
                        <select
                          value={c.status}
                          disabled={atualizando === c.id}
                          onChange={e => mudarStatus(c.id, e.target.value)}
                          className={`s-${c.status}`}
                        >
                          {STATUS_OPCOES.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                      ) : (
                        <span className={`sb s-${c.status}`}>{c.status}</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        onClick={() => baixarContrato(c)}
                        disabled={baixando === c.id}
                        className="btn-amber"
                        style={{ fontSize: '12px', padding: '5px 12px' }}
                      >
                        {baixando === c.id ? '...' : 'Baixar'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : (
          termos.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Nenhum termo gerado ainda.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  {['Cliente', 'Veículo', 'Valor da Dívida', 'Pgto. até', 'Assinatura', 'Gerado por', ''].map(h => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {termos.map(t => (
                  <tr key={t.id}>
                    <td style={{ fontWeight: 'bold' }}>{t.clientes?.nome || '—'}</td>
                    <td>{t.veiculos?.placa} · {t.veiculos?.modelo}</td>
                    <td>R$ {parseFloat(String(t.valor_divida).replace(/\./g, '').replace(',', '.') || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                    <td>{formatarData(t.data_pagamento)}</td>
                    <td>{formatarData(t.data_assinatura)}</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{t.usuarios?.nome || '—'}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        onClick={() => baixarTermo(t)}
                        disabled={baixando === t.id}
                        className="btn-amber"
                        style={{ fontSize: '12px', padding: '5px 12px' }}
                      >
                        {baixando === t.id ? '...' : 'Baixar'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>
    </div>
  )
}
